import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Grid, Typography, Select, MenuItem, Box, FormControl, FormHelperText, InputAdornment, TextField, Button, ListSubheader, Autocomplete, Link, Divider } from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { countryManagementCreateOrUpdate, countryManagementMetaData, countryManagementGetById } from '../countryManagement.service';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useParams } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import { Icon } from "@iconify/react";
import Loader from '../../../components/loader';
import AlertToast from '../../../components/alertToast';
import AdministrativeLocationTypesManager from '../components/AdministrativeLocationTypesManager';
import GeographicalDataLoader from '../components/GeographicalDataLoader';
import {
  createMenuProps,
  FieldLabel,
  formFieldSx,
  helperTextSx,
  sectionTitleSx,
} from '../../../styles/formStyles';
import { primaryIconColor } from '../../../styles/iconStyles';
import {
  createEditBackButtonSx,
  createEditCancelButtonSx,
  createEditCardSx,
  createEditFooterSx,
  createEditPageHeaderSx,
  createEditPageShellSx,
  createEditPageTitleSx,
  createEditPrimaryButtonSx,
} from '../../../styles/pageFormStyles';

const MenuProps = createMenuProps();
const fieldSx = formFieldSx;

function CountryManagementCreation() {
  const location = useLocation();
  const { id } = useParams();
  const queryParams = new URLSearchParams(location.search);
  const navigate = useNavigate();
  const type = queryParams.get("type");
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [openAlert, setOpenAlert] = useState(false);
  const [alert, setAlert] = useState('');
  const [responseMsg, setResponseMsg] = useState("");
  const [countriesMetadata, setCountriesMetadata] = useState([]);
  const [disableField, setDisableField] = useState(false);
  const [administrativeLocationTypes, setAdministrativeLocationTypes] = useState([]);
  // Ids of administrative levels the admin confirmed removing despite still-recorded locations, via
  // the "locations are still tied to this level" modal inside AdministrativeLocationTypesManager.
  // Nothing is deactivated until the form is actually submitted — see onSubmit.
  const [cascadeDeactivateLevelIds, setCascadeDeactivateLevelIds] = useState([]);

  const ALERT_DURATION = 2000;
  const alertClose = () => {
    setAlert('')
  }

  const showErrorAlert = (message) => {
    setResponseMsg(message);
    setAlert('false');
    setOpenAlert(true);
    setTimeout(alertClose, ALERT_DURATION);
  };
  const coruntrySchema = z.object({
    name: z.string().min(1, { message: "Country Name is required" }),
    regionName: z.string().min(1, { message: "Region is required" }),
    currencyCode: z.string().min(1, { message: "Currency is required" }),
    isoCode: z.string().min(1, { message: "Country Code is required" }),
    timeZone: z.string().min(1, { message: "Timezone is required" }),
    termsAndConditions: z.union([z.instanceof(File), z.string()]).optional().nullable(),
    privacyPolicy: z.union([z.instanceof(File), z.string()]).optional().nullable(),
    administrativeLocationTypes: z.array(z.object({
      id: z.string().uuid().optional().nullable(),
      name: z.string().min(1, { message: "Level name is required" }),
      level: z.number().min(1, { message: "Level must be a positive number" }),
    })).optional().default([]),
  });

  const { handleSubmit, formState: { errors }, control, setValue } = useForm({ resolver: zodResolver(coruntrySchema), mode: "onChange" });

  const fetchData = useCallback(async () => {
    if (editMode === true) {
      countryManagementGetById(id).then(async (res) => {
        let countryListData = res.data
        setDisableField(Boolean(countryListData?.countryLinkage))
        setValue('name', countryListData?.name);
        setValue('regionName', countryListData?.regionName);
        setValue('currencyCode', countryListData?.currencyCode || countryListData?.currency);
        setValue('timeZone', countryListData?.timeZone);
        setValue('isoCode', countryListData?.isoCode);
        setValue('termsAndConditions', countryListData?.termsAndConditionsUrl || countryListData?.termsAndConditions);
        setValue('privacyPolicy', countryListData?.privacyPolicyUrl || countryListData?.privacyPolicy);
        if (countryListData?.administrativeLocationTypes && countryListData.administrativeLocationTypes.length > 0) {
          setAdministrativeLocationTypes(countryListData.administrativeLocationTypes.map(alt => ({
            id: alt.id,
            level: alt.level,
            name: alt.name,
          })));
          setValue('administrativeLocationTypes', countryListData.administrativeLocationTypes.map(alt => ({
            id: alt.id,
            level: alt.level,
            name: alt.name,
          })));
        }
      })
    }
  }, [editMode, id, setValue]);

  const fetchCountriesMetadata = useCallback(async () => {
    await countryManagementMetaData(id, editMode).then((res) => {
      if (res) {
        setCountriesMetadata(res.data);
      } else {
        setCountriesMetadata([]);
      }
    })
  }, [editMode, id, setCountriesMetadata])

  useEffect(() => {
    if (type === 'edit') {
      setEditMode(true);
    }
  }, [type]);

  useEffect(() => {
    if (type === 'edit') {
      fetchData();
    }
  }, [type, fetchData]);

  useEffect(() => {
    fetchCountriesMetadata();
  }, [fetchCountriesMetadata]);

  const countryOptions = useMemo(() => {
    const countryMap = new Map();

    countriesMetadata.forEach((item) => {
      const countryName = item.countryName;
      if (!countryName || countryMap.has(countryName)) return;

      countryMap.set(countryName, {
        id: item.id,
        name: countryName,
        regionName: item.regionName || 'Other',
        subregionName: item.subregionName,
        currencyCode: item.currencyCode,
        currencySymbol: item.currencySymbol,
        isoCode: item.isoCode,
        timeZone: item.timeZone,
        phoneCode: item.phoneCode,
        flagSymbol: item.flagSymbol,
      });
    });

    return Array.from(countryMap.values()).sort((a, b) => {
      const regionCompare = a.regionName.localeCompare(b.regionName);
      if (regionCompare !== 0) return regionCompare;
      return a.name.localeCompare(b.name);
    });
  }, [countriesMetadata]);

  const groupedRegionOptions = useMemo(() => {
    const regionMap = new Map();

    countriesMetadata.forEach((item) => {
      const parentRegion = item.regionName || 'Other';
      const subregionValue = item.subregionName || item.regionName;
      if (!subregionValue) return;

      if (!regionMap.has(parentRegion)) {
        regionMap.set(parentRegion, new Set());
      }
      regionMap.get(parentRegion).add(subregionValue);
    });

    return Array.from(regionMap.entries())
      .map(([region, subregions]) => ({
        region,
        subregions: Array.from(subregions).sort((a, b) => a.localeCompare(b)),
      }))
      .sort((a, b) => a.region.localeCompare(b.region));
  }, [countriesMetadata]);

  const uniqueCurrencyOptions = useMemo(() => {
    const currencyMap = new Map();
    countriesMetadata.forEach((item) => {
      if (item.currencyCode && !currencyMap.has(item.currencyCode)) {
        currencyMap.set(item.currencyCode, item.currencySymbol || '');
      }
    });
    return Array.from(currencyMap.entries())
      .map(([code, symbol]) => ({ code, symbol }))
      .sort((a, b) => a.code.localeCompare(b.code));
  }, [countriesMetadata]);

  const uniqueTimeZoneOptions = useMemo(() => {
    return Array.from(
      new Set(countriesMetadata.map((item) => item.timeZone).filter(Boolean))
    );
  }, [countriesMetadata]);

  const handleCountryChange = (selectedCountryName, fieldOnChange) => {
    fieldOnChange(selectedCountryName);

    const selectedCountry = countriesMetadata.find(
      (item) => item.countryName === selectedCountryName
    );

    if (selectedCountry) {
      setValue("regionName", selectedCountry.subregionName || selectedCountry.regionName || "", { shouldValidate: true });
      setValue("currencyCode", selectedCountry.currencyCode || "", { shouldValidate: true });
      setValue("timeZone", selectedCountry.timeZone || "", { shouldValidate: true });
      setValue("countryId", selectedCountry.id, { shouldValidate: false });
      setValue("isoCode", selectedCountry.isoCode || "", { shouldValidate: true });
    }
  };

  const onSubmit = async (data) => {
    try {
      setLoading(true)
      const selectedCountry = countriesMetadata.find((item) => item.countryName === data.name);

      const formData = new FormData();

      formData.append("name", data.name);
      formData.append("regionName", data.regionName);
      formData.append("currencyCode", data.currencyCode);
      formData.append("isoCode", data.isoCode);
      formData.append("timeZone", data.timeZone);
      formData.append("countryId", selectedCountry?.id || "");
      formData.append("phoneCode", selectedCountry?.phoneCode || "");
      formData.append("currencySymbol", selectedCountry?.currencySymbol || "");
      formData.append("countryFlag", selectedCountry?.flagSymbol || "");

      if (data.administrativeLocationTypes && data.administrativeLocationTypes.length > 0) {
        formData.append("administrativeLocationTypes", JSON.stringify(
          data.administrativeLocationTypes.map((alt, index) => ({
            id: alt.id || null,
            name: alt.name,
            level: index + 1,
          }))
        ));
      } else {
        formData.append("administrativeLocationTypes", JSON.stringify([]));
      }

      cascadeDeactivateLevelIds.forEach((levelId) => {
        formData.append("cascadeDeactivateLocationTypeIds", levelId);
      });

      if (data.termsAndConditions instanceof File) {
        formData.append("termsAndConditionsPdf", data.termsAndConditions);
      }

      if (data.privacyPolicy instanceof File) {
        formData.append("privacyPolicyPdf", data.privacyPolicy);
      }

      countryManagementCreateOrUpdate(formData, editMode, id).then((res) => {
        if (res.success) {
          setResponseMsg("Country details saved successfully");
          setAlert('true');
          setOpenAlert(true)
          setLoading(false)
          setTimeout(() => {
            editMode
              ? navigate(`/countryManagementView/${id}?type=edit`)
              : navigate('/countryManagement');
            alertClose();
          }, ALERT_DURATION);
        } else {
          setResponseMsg(res.error?.message || "Failed to save country details");
          setAlert('false');
          setLoading(false)
          setOpenAlert(true)
          setTimeout(() => {
            alertClose()
          }, ALERT_DURATION);
        }
      })

    } catch (error) {
      console.error(error);
    }
  };

  const handlePdfUpload = (e, onChange) => {
    const file = e.target.files?.[0];
    const size = 5 * 1024 * 1024;

    if (file) {
      if (file.type !== "application/pdf") {
        onChange(null);
      } else if (file.size > size) {
        onChange(null);
        setResponseMsg("File size should not exceed 5MB");
      } else {
        onChange(file);
        return;
      }
      setAlert('false');
      setOpenAlert(true);
      setLoading(false);
      setTimeout(() => {
        alertClose();
      }, ALERT_DURATION);
      e.target.value = "";
    }
  };

  const getFileName = (fileOrPath) => {
    if (!fileOrPath) return "";
    
    let fileName = "";
    if (fileOrPath instanceof File) {
      fileName = fileOrPath.name;
    } else if (typeof fileOrPath === "string") {
      // Remove query parameters and get the last part
      const urlWithoutParams = fileOrPath.split("?")[0];
      fileName = urlWithoutParams.split("/").pop();
    }
    
    if (!fileName) return "";
    
    // Decode URL-encoded characters if needed
    try {
      fileName = decodeURIComponent(fileName);
    } catch (e) {
      // If decoding fails, use the original
    }
    
    // Remove UUID prefix (everything before the first underscore)
    const underscoreIndex = fileName.indexOf('_');
    if (underscoreIndex !== -1) {
      fileName = fileName.substring(underscoreIndex + 1);
    }
    
    return fileName;
  };

  const isFileUrl = (fileOrPath) => {
    return typeof fileOrPath === 'string' && (fileOrPath.startsWith('http://') || fileOrPath.startsWith('https://'));
  };

  const downloadFile = (fileUrl) => {
    if (isFileUrl(fileUrl)) {
      const sasToken = process.env.REACT_APP_AZURE_SAS_TOKEN;
      const urlWithSas = `${fileUrl}?${sasToken}`;
      const link = document.createElement('a');
      link.href = urlWithSas;
      link.download = getFileName(fileUrl);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleBack = () => {
    editMode ? navigate(`/countryManagementView/${id}?type=edit`) : navigate('/countryManagement');
  };


  return (
    <Box sx={createEditPageShellSx}>
      <Box sx={createEditPageHeaderSx}>
        <Button
          onClick={handleBack}
          startIcon={<Icon icon="mdi:arrow-left" width="20" height="20" />}
          sx={createEditBackButtonSx}
        >
          Back
        </Button>
        <Grid container alignItems={'center'}>
          <Grid size={12}>
            <Typography className="app-page-title" sx={createEditPageTitleSx}>
              {editMode ? 'Update Country' : 'Add New Country'}
            </Typography>
          </Grid>
        </Grid>
      </Box>

      <form onSubmit={handleSubmit(onSubmit)} className="material-form">
        <Box sx={createEditCardSx}>
          <Typography sx={sectionTitleSx}>Country Details</Typography>
          <Grid container spacing={3} alignItems="flex-start">
            <Grid size={12}>
              <Box>
                <FieldLabel label="Country Name" required />
                <FormControl fullWidth error={!!errors.name} sx={helperTextSx}>
                  <Controller name="name" control={control} defaultValue=""
                    render={({ field }) => {
                      const selectedOption = countryOptions.find((option) => option.name === field.value) || null;

                      return (
                        <Autocomplete
                          options={countryOptions}
                          groupBy={(option) => option.regionName}
                          getOptionLabel={(option) => option?.name || ''}
                          isOptionEqualToValue={(option, value) => option.name === value.name}
                          value={selectedOption}
                          disabled={disableField}
                          onChange={(_, newValue) => {
                            handleCountryChange(newValue?.name || '', field.onChange);
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              id="country-select"
                              placeholder="Search or select country name"
                              error={!!errors.name}
                              helperText={errors.name?.message}
                              sx={fieldSx}
                            />
                          )}
                        />
                      );
                    }}
                  />
                </FormControl>
              </Box>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Box>
                <FieldLabel label="Region" required />
                <FormControl fullWidth error={!!errors.regionName} sx={helperTextSx}>
                  <Controller name="regionName" control={control} defaultValue={''}
                    render={({ field, fieldState: { error } }) => (
                      <Select displayEmpty MenuProps={MenuProps} IconComponent={(iconProps) => <Icon icon="mdi:chevron-down" width="22" height="22" color={primaryIconColor} {...iconProps} />} sx={fieldSx} id="country-select" disabled={disableField} {...field} error={!!error}>
                        <MenuItem value="" disabled>Select region</MenuItem>
                        {groupedRegionOptions.flatMap((regionGroup) => [
                          <ListSubheader key={`group-${regionGroup.region}`}>{regionGroup.region}</ListSubheader>,
                          ...regionGroup.subregions.map((subregion) => (
                            <MenuItem key={`${regionGroup.region}-${subregion}`} value={subregion}>
                              {subregion}
                            </MenuItem>
                          )),
                        ])}
                      </Select>)} />
                  {!!errors.regionName && (<FormHelperText error>{errors.regionName.message}</FormHelperText>)}
                </FormControl>
              </Box>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Box>
                <FieldLabel label="Currency" required />
                <FormControl fullWidth error={!!errors.currencyCode} sx={helperTextSx}>
                  <Controller name="currencyCode" control={control} defaultValue=""
                    render={({ field, fieldState: { error } }) => (
                      <Select displayEmpty MenuProps={MenuProps} IconComponent={(iconProps) => <Icon icon="mdi:chevron-down" width="22" height="22" color={primaryIconColor} {...iconProps} />} sx={fieldSx} id="currency-select" disabled={disableField} {...field} error={!!error}>
                        <MenuItem value="" disabled>Select currency</MenuItem>
                        {uniqueCurrencyOptions.map((currencyOption) => (
                          <MenuItem key={currencyOption.code} value={currencyOption.code}>
                            {currencyOption.code} {currencyOption.symbol ? `(${currencyOption.symbol})` : ''}
                          </MenuItem>
                        ))}
                      </Select>)} />
                  {!!errors.currencyCode && (<FormHelperText error>{errors.currencyCode.message}</FormHelperText>)}
                </FormControl>
              </Box>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Box>
                <FieldLabel label="Country Code (ISO)" required />
                <FormControl fullWidth sx={helperTextSx}>
                  <Controller name="isoCode" control={control} defaultValue=""
                    render={({ field, fieldState: { error } }) => (
                      <TextField {...field} id="isoCode" placeholder="e.g., IN, US, KE..." sx={fieldSx} disabled={disableField} type="text" variant="outlined" error={!!error} helperText={error ? error.message : ''} inputProps={{
                        maxLength: 5
                      }}
                        onChange={(e) => { const value = e.target.value.toUpperCase().replace(/[^A-Z]/g, ''); field.onChange(value) }}
                      />
                    )}
                  />
                </FormControl>
              </Box>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Box>
                <FieldLabel label="Timezone" required />
                <FormControl fullWidth error={!!errors.timeZone} sx={helperTextSx}>
                  <Controller name="timeZone" control={control} defaultValue=""
                    render={({ field, fieldState: { error } }) => (
                      <Select displayEmpty MenuProps={MenuProps} IconComponent={(iconProps) => <Icon icon="mdi:chevron-down" width="22" height="22" color={primaryIconColor} {...iconProps} />} sx={fieldSx} id="currency-select" disabled={disableField} {...field} error={!!error}>
                        <MenuItem value="" disabled>Select timezone</MenuItem>
                        {uniqueTimeZoneOptions.map((timeZoneValue) => (
                          <MenuItem key={timeZoneValue} value={timeZoneValue}>
                            {timeZoneValue}
                          </MenuItem>
                        ))}
                      </Select>)} />
                  {!!errors.timeZone && (<FormHelperText error>{errors.timeZone.message}</FormHelperText>)}
                </FormControl>
              </Box>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Box>
                <FieldLabel label="Terms and Conditions" />
                <FormControl fullWidth sx={helperTextSx}>
                  <Controller name="termsAndConditions" control={control} defaultValue={null}
                    render={({ field }) => (
                      <>
                        <input type="file" id="termsAndConditions" accept="application/pdf" hidden onChange={(e) => handlePdfUpload(e, field.onChange)} />
                        <TextField
                          fullWidth
                          value={getFileName(field.value)}
                          sx={{
                            ...fieldSx,
                            '& .MuiInputBase-input': {
                              overflow: 'hidden',
                              textOverflow: 'clip',
                              whiteSpace: 'nowrap',
                            },
                          }}
                          variant="outlined"
                          placeholder="Attach the file in PDF format"
                          InputProps={{
                            readOnly: true,
                            endAdornment: (
                              <InputAdornment position="end" sx={{ gap: 1, display: 'flex', alignItems: 'center' }}>
                                {isFileUrl(field.value) && (
                                  <>
                                    <Divider orientation="vertical" flexItem sx={{ my: 0.5, borderColor: 'rgba(0, 0, 0, 0.23)' }} />
                                    <Link
                                      component="button"
                                      type="button"
                                      onClick={(e) => { e.preventDefault(); downloadFile(field.value); }}
                                      sx={{ cursor: 'pointer', fontSize: '0.85rem', color: '#294190', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                                    >
                                      Download
                                    </Link>
                                  </>
                                )}
                                <label htmlFor="termsAndConditions" style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                                  <Box component="img" src="/assets/Attachment.svg" alt="attachIcon" />
                                </label>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </>
                    )}
                  />
                </FormControl>
              </Box>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Box>
                <FieldLabel label="Privacy Policy" />
                <FormControl fullWidth sx={helperTextSx}>
                  <Controller name="privacyPolicy" control={control} defaultValue={null} render={({ field }) => (
                    <>
                      <input type="file" id="privacyPolicy" accept="application/pdf" hidden onChange={(e) => handlePdfUpload(e, field.onChange)} />
                      <TextField
                        fullWidth
                        value={getFileName(field.value)}
                        sx={{
                          ...fieldSx,
                          '& .MuiInputBase-input': {
                            overflow: 'hidden',
                            textOverflow: 'clip',
                            whiteSpace: 'nowrap',
                          },
                        }}
                        variant="outlined"
                        placeholder="Attach the file in PDF format"
                        InputProps={{
                          readOnly: true,
                          endAdornment: (
                            <InputAdornment position="end" sx={{ gap: 1, display: 'flex', alignItems: 'center' }}>
                              {isFileUrl(field.value) && (
                                <>
                                  <Divider orientation="vertical" flexItem sx={{ my: 0.5, borderColor: 'rgba(0, 0, 0, 0.23)' }} />
                                  <Link
                                    component="button"
                                    type="button"
                                    onClick={(e) => { e.preventDefault(); downloadFile(field.value); }}
                                    sx={{ cursor: 'pointer', fontSize: '0.85rem', color: '#294190', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                                  >
                                    Download
                                  </Link>
                                </>
                              )}
                              <label htmlFor="privacyPolicy" style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                                <Box component="img" src="/assets/Attachment.svg" alt="attachIcon" />
                              </label>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </>
                  )}
                  />
                </FormControl>
              </Box>
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          <Typography sx={sectionTitleSx}>Administrative Location Types</Typography>
          <Grid container spacing={3} alignItems="flex-start">
            <Grid size={12}>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Define the administrative hierarchy levels for this country (e.g., State, District, Block)
              </Typography>
              <Controller
                name="administrativeLocationTypes"
                control={control}
                render={({ field }) => (
                  <AdministrativeLocationTypesManager
                    levels={administrativeLocationTypes}
                    onChange={(newLevels) => {
                      setAdministrativeLocationTypes(newLevels);
                      field.onChange(newLevels);
                    }}
                    readOnly={false}
                    countryId={editMode ? id : null}
                    onCascadeConfirm={(levelId) =>
                      setCascadeDeactivateLevelIds((prev) => (prev.includes(levelId) ? prev : [...prev, levelId]))
                    }
                    onError={showErrorAlert}
                  />
                )}
              />
            </Grid>
          </Grid>

          {/* Only once the country exists. The load is keyed on the saved country's ISO code, so on a
              country still being filled in there is nothing to load against. A country created here
              loads its geography by itself; this is the place to retry or refresh it. */}
          {editMode && id && (
            <>
              <Divider sx={{ my: 3 }} />

              <Typography sx={sectionTitleSx}>Geographical Data</Typography>
              <Grid container spacing={3} alignItems="flex-start">
                <Grid size={12}>
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                    The actual states, districts and blocks recorded under the levels above
                  </Typography>
                  <GeographicalDataLoader countryId={id} />
                </Grid>
              </Grid>
            </>
          )}

          <Box sx={{ ...createEditFooterSx, flexDirection: { xs: 'column-reverse', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'center' } }}>
            <Box sx={{ width: { xs: '100%', sm: 'auto' }, display: 'flex', justifyContent: { xs: 'stretch', sm: 'flex-start' } }}>
              <Button onClick={() => { navigate('/countryManagement') }} sx={{ ...createEditCancelButtonSx, width: { xs: '100%', sm: 'auto' }, justifyContent: { xs: 'center', sm: 'flex-start' } }}>
                Cancel
              </Button>
            </Box>
            <Box sx={{ width: { xs: '100%', sm: 'auto' }, display: 'flex', justifyContent: { xs: 'stretch', sm: 'flex-end' }, ml: { sm: 'auto' } }}>
              <Button
                variant="contained"
                type="submit"
                sx={{ ...createEditPrimaryButtonSx, width: { xs: '100%', sm: 'auto' } }}
              >
                {editMode ? "Update Country" : "Create Country"}
              </Button>
            </Box>
          </Box>
        </Box>
      </form>

      {loading && <Loader />}

      <Box textAlign={'end'}>
        <AlertToast open={openAlert && alert === 'true'} onClose={alertClose} message={responseMsg} variant="success" />
        <AlertToast open={openAlert && alert === 'false'} onClose={alertClose} message={responseMsg} variant="error" />
      </Box>
    </Box>
  )
}

export default CountryManagementCreation
